import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider, googleLogout } from '@react-oauth/google';
import axios from 'axios';
import { API } from '../config/api';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Initialize axios instance
  const api = axios.create({
    baseURL: API,
  });

  const [user, setUser] = useState(() => {
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      if (token && userData && token !== 'null' && token !== 'undefined') {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return JSON.parse(userData);
      }
      return null;
    } catch (err) {
      console.error('Failed to parse stored user', err);
      return null;
    }
  });

  // loading tracks whether we are initializing auth from storage/remote
  const [loading, setLoading] = useState(true);

  // Google login function
  const googleLogin = async (googleData) => {
    try {
      // Save current cart before login
      const currentCart = localStorage.getItem('noureeshCart');

      // Verify the token with your backend
      const response = await api.post('/auth/google', {
        token: googleData.access_token
      });
      
      // Store both token and user data
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      // Set axios defaults and state
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setUser(response.data.user);

      // Restore cart after successful login
      if (currentCart) {
        localStorage.setItem('noureeshCart', currentCart);
      }
      
      // Return the complete response including needsProfileCompletion
      return {
        success: true,
        user: response.data.user,
        token: response.data.token,
        needsProfileCompletion: response.data.needsProfileCompletion || 
                              (!response.data.user.phone)
      };
    } catch (error) {
      console.error('Google login failed:', error);
      throw error;
    }
  };

  // useCallback makes logout stable
  const logout = useCallback(() => {
    // Clear all auth-related data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    googleLogout();
  }, [api]);

  // session synchronization
  useEffect(() => {
    const syncSession = () => {
      const token = localStorage.getItem('token');
      if (!token && user) {
        logout();
      }
    };
    
    window.addEventListener('storage', syncSession);
    return () => window.removeEventListener('storage', syncSession);
  }, [user, logout]);

  // Add request interceptor to include token
  api.interceptors.request.use(config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn('No token available for request'); // Debug log
    }
    return config;
  }, error => {
    return Promise.reject(error);
  });

  useEffect(() => {
    const initializeAuth = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (token && token !== 'null' && token !== 'undefined') {
        // Set the token in axios headers immediately
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        try {
          const res = await api.get('/auth/me');
          // Prefer server authoritative user data, but fallback to stored user
          if (res && res.data) {
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
          }
        } catch (err) {
          console.error('Auto-login failed:', err);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Legacy restore: if user wasn't set during initialization, restore from storage
  useEffect(() => {
    if (!user) {
      try {
        const token = localStorage.getItem('token');
        const userData = localStorage.getItem('user');
        if (token && userData && token !== 'null' && token !== 'undefined') {
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          setUser(JSON.parse(userData));
        }
      } catch (err) {
        console.error('Failed to restore auth from localStorage', err);
      }
    }
  }, [user, api.defaults.headers.common]);

  const login = async (identifier, password) => {
    try {
      // Save current cart before login
      const currentCart = localStorage.getItem('noureeshCart');

      const response = await api.post('/auth/login', { 
        identifier: identifier,
        password: password
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setUser(response.data.user);

      // Restore cart after successful login
      if (currentCart) {
        localStorage.setItem('noureeshCart', currentCart);
      }

      return response.data.user;
    } catch (error) {
      console.error('Login failed:', error);
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  };

  const signup = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      api.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      setUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error('Signup failed:', error);
      throw new Error(error.response?.data?.message || 'Signup failed');
    }
  };

  const resetPassword = async (email) => {
    try {
      const response = await api.post('/auth/forgot-password', { email });
      return response.data; // Typically returns { message: "Reset email sent" }
    } catch (error) {
      console.error('Password reset failed:', error);
      throw new Error(error.response?.data?.message || 'Password reset failed');
    }
  };

  const resetPasswordConfirm = async (email, token, newPassword) => {
    try {
      const response = await api.post('auth/reset-password', {
        email,
        token,
        newPassword
      });
      return response.data;
    } catch (error) {
      console.error('Password reset failed:', error);
      throw new Error(error.response?.data?.message || 'Password reset failed');
    }
  };

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <AuthContext.Provider value={{ user, loading, login, signup, logout, googleLogin, resetPassword, resetPasswordConfirm, api, setUser }}>
        {children}
      </AuthContext.Provider>
    </GoogleOAuthProvider>
  );
};

export const useAuth = () => useContext(AuthContext);
